/**
 * Interest Rates Hooks
 * TanStack Query hooks for crypto interest rates
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    getInterestRates,
    updateInterestRate,
    bulkUpdateInterestRates,
} from '@/services/interest-rates.service'
import type {
    CryptoInterestRate,
    UpdateCryptoInterestRateRequest,
    BulkUpdateCryptoInterestRatesRequest,
} from '@/types/interest-rate'

export const useInterestRates = () => {
    return useQuery<CryptoInterestRate[]>({
        queryKey: ['interest-rates'],
        queryFn: getInterestRates,
        staleTime: 1000 * 60 * 2,
    })
}

export const useUpdateInterestRate = () => {
    const queryClient = useQueryClient()

    return useMutation<
        CryptoInterestRate,
        unknown,
        { cryptoId: string; data: UpdateCryptoInterestRateRequest }
    >({
        mutationFn: ({ cryptoId, data }) => updateInterestRate(cryptoId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['interest-rates'] })
        },
    })
}

export const useBulkUpdateInterestRates = () => {
    const queryClient = useQueryClient()

    return useMutation<
        CryptoInterestRate[],
        unknown,
        BulkUpdateCryptoInterestRatesRequest
    >({
        mutationFn: data => bulkUpdateInterestRates(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['interest-rates'] })
        },
    })
}
